// Performance Trajectory — D3 SVG line chart (Phase 3).
//
// One team's xGF (green) vs xGA (red) over its last N matches, optionally as a
// cumulative rolling average. React owns the container + the <svg> skeleton (the
// groups + the two line paths + the legend); D3 owns the grids/lines/dots via
// keyed enter/update/exit joins in a useEffect (a port of the legacy draw), so
// everything animates to new positions on a control change. The skeleton
// persists across control changes (like Goals by Time Band), so the first draw
// for the mount snaps and later draws animate. The HTML y-axis + the dot
// tooltips are rebuilt each draw. ResizeObserver redraws (no transition) on
// resize, cleaned up in the effect return. Fixture + team + timeline + view from
// the context.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { scaleLinear as d3ScaleLinear } from "d3-scale";
import { line as d3Line } from "d3-shape";
import { easeCubicInOut } from "d3-ease";
import { selectTrajectory, trajTooltip, type TrajectoryShape } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

// The scale + line generators are typed with minimal locals matching exactly
// how this file calls them (see HalfDonut for the same pattern with d3-shape).
interface LinearScale {
  (v: number): number;
  domain(d: [number, number]): LinearScale;
  range(r: [number, number]): LinearScale;
}
interface LineGen {
  (data: number[]): string | null;
  x(fn: (d: number, i: number) => number): LineGen;
  y(fn: (d: number) => number): LineGen;
}
const scaleLinear = d3ScaleLinear as unknown as () => LinearScale;
const line = d3Line as unknown as () => LineGen;

const PAD = { l: 2, r: 6, t: 8, b: 8 };
const DURATION = 450;

/** [start, stop) integers — a local stand-in for d3.range (identical output). */
function intRange(start: number, stop: number): number[] {
  const out: number[] = [];
  for (let i = start; i < stop; i++) out.push(i);
  return out;
}

function draw(
  chartEl: HTMLElement,
  shape: TrajectoryShape,
  useTransition: boolean
): void {
  const svgNode = chartEl.querySelector<SVGSVGElement>("svg.traj-svg");
  const yaxisEl = chartEl.querySelector<HTMLElement>(".traj-yaxis");
  if (!svgNode || !yaxisEl) return;
  const W = svgNode.clientWidth;
  const H = svgNode.clientHeight;
  if (W < 10 || H < 10) return; // not laid out yet — the ResizeObserver retries

  const { xgfVals, xgaVals, maxY, n: N } = shape;
  const { l: padL, r: padR, t: padT, b: padB } = PAD;

  // Line thickness + dot size scale with the module area, clamped.
  const areaSc = Math.sqrt((W * H) / (250 * 100));
  const strokeW = Math.max(1.2, Math.min(3.5, 1.8 * areaSc));
  const dotR = Math.max(1.875, Math.min(6.25, 2.75 * areaSc));

  const xScale = scaleLinear().domain([0, Math.max(N - 1, 1)]).range([padL, W - padR]);
  const yScale = scaleLinear().domain([0, maxY]).range([H - padB, padT]);
  const lineGen = line().x((_, i) => xScale(i)).y((d) => yScale(d));
  const ticks = intRange(0, maxY + 1);
  const dur = useTransition ? DURATION : 0;
  const ease = easeCubicInOut;

  // Y-axis: HTML spans positioned by percentage (never scale with the SVG).
  yaxisEl.innerHTML = ticks
    .map(
      (tick) =>
        `<span style="top:${((yScale(tick) / H) * 100).toFixed(2)}%">${tick}</span>`
    )
    .join("");

  const svg = select(svgNode);

  // Y gridlines (horizontal).
  const gridSel = svg
    .select<SVGGElement>(".traj-grid")
    .selectAll<SVGLineElement, number>("line")
    .data(ticks);
  gridSel
    .enter()
    .append("line")
    .attr("stroke-width", (d) => (d === 0 ? 1 : 0.5))
    .attr("stroke", (d) => (d === 0 ? "#555" : "#383838"))
    .attr("stroke-dasharray", "4,4")
    .attr("x1", padL)
    .attr("x2", W - padR)
    .attr("y1", (d) => yScale(d))
    .attr("y2", (d) => yScale(d))
    .merge(gridSel)
    .attr("stroke-width", (d) => (d === 0 ? 1 : 0.5))
    .attr("stroke", (d) => (d === 0 ? "#555" : "#383838"))
    .attr("stroke-dasharray", "4,4")
    .attr("x1", padL)
    .attr("x2", W - padR)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("y1", (d) => yScale(d))
    .attr("y2", (d) => yScale(d));
  gridSel.exit().remove();

  // X gridlines (vertical, one per match).
  const yIndices = intRange(0, N);
  const xgridSel = svg
    .select<SVGGElement>(".traj-xgrid")
    .selectAll<SVGLineElement, number>("line")
    .data(yIndices);
  xgridSel
    .enter()
    .append("line")
    .attr("stroke-width", 0.5)
    .attr("stroke", "#383838")
    .attr("stroke-dasharray", "4,4")
    .attr("y1", padT)
    .attr("y2", H - padB)
    .attr("x1", (i) => xScale(i))
    .attr("x2", (i) => xScale(i))
    .merge(xgridSel)
    .attr("stroke-width", 0.5)
    .attr("stroke", "#383838")
    .attr("stroke-dasharray", "4,4")
    .attr("y1", padT)
    .attr("y2", H - padB)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("x1", (i) => xScale(i))
    .attr("x2", (i) => xScale(i));
  xgridSel.exit().remove();

  svg
    .select<SVGPathElement>(".traj-line-xgf")
    .attr("stroke-width", strokeW)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("d", lineGen(xgfVals) ?? "");
  svg
    .select<SVGPathElement>(".traj-line-xga")
    .attr("stroke-width", strokeW)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("d", lineGen(xgaVals) ?? "");

  function updateDots(
    groupSelector: string,
    vals: number[],
    stat: "xgf" | "xga"
  ): void {
    const rows = vals.map((v, i) => ({ v, i }));
    const dots = svg
      .select<SVGGElement>(groupSelector)
      .selectAll<SVGCircleElement, { v: number; i: number }>("circle")
      .data(rows, (d) => String(d.i));
    dots
      .enter()
      .append("circle")
      .attr("class", "chart-dot")
      .attr("fill", "#ccc")
      .attr("r", dotR)
      .attr("cx", (d) => xScale(d.i))
      .attr("cy", (d) => yScale(d.v))
      .merge(dots)
      .attr("r", dotR)
      .attr("fill", "#ccc")
      .each(function (this: SVGCircleElement, d) {
        this.setAttribute("data-tooltip", trajTooltip(shape, stat, d.i));
      })
      .transition()
      .duration(dur)
      .ease(ease)
      .attr("cx", (d) => xScale(d.i))
      .attr("cy", (d) => yScale(d.v));
    dots.exit().remove();
  }
  updateDots(".traj-dots-xgf", xgfVals, "xgf");
  updateDots(".traj-dots-xga", xgaVals, "xga");
}

export interface TrajectoryProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
  view?: string | null;
}

export default function Trajectory({
  fixture,
  team,
  timeline,
  view,
}: TrajectoryProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef(true);

  const { data, isPending, isError, refetch } = useWidgetData(
    "performance-trajectory",
    useFixtureKey(fixture)
  );

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const chosen = team || (parts[0] ?? "").trim();
  const shape = selectTrajectory(
    data?.series ?? {},
    data?.matchInfo ?? {},
    chosen,
    timeline,
    view
  );
  const sig = JSON.stringify(shape);

  useEffect(() => {
    const chartEl = chartRef.current;
    if (!chartEl || !shape) return;
    const useTransition = !firstRef.current;
    firstRef.current = false;
    draw(chartEl, shape, useTransition);

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(chartEl, shape, false));
    ro.observe(chartEl);
    return () => ro.disconnect();
    // `sig` captures the shaped series (the complete draw input); `shape` fresh.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!shape) {
    return (
      <div className="data-wrapper">
        <p className="module-empty">No data</p>
      </div>
    );
  }

  return (
    <div className="data-wrapper traj-wrapper">
      <div className="legend traj-legend">
        <div className="legend-item">
          <svg className="legend-line-svg">
            <line x1="1" y1="4" x2="17" y2="4" stroke="#2ecc71" strokeWidth="2" strokeLinecap="round" />
          </svg>
          xGF
        </div>
        <div className="legend-item">
          <svg className="legend-line-svg">
            <line x1="1" y1="4" x2="17" y2="4" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" />
          </svg>
          xGA
        </div>
      </div>
      <div className="traj-chart" ref={chartRef}>
        <div className="traj-yaxis" />
        <svg className="traj-svg">
          <g className="traj-xgrid" />
          <g className="traj-grid" />
          <path className="traj-line-xga" fill="none" stroke="#e74c3c" strokeLinecap="round" strokeLinejoin="round" />
          <path className="traj-line-xgf" fill="none" stroke="#2ecc71" strokeLinecap="round" strokeLinejoin="round" />
          <g className="traj-dots-xga" />
          <g className="traj-dots-xgf" />
        </svg>
      </div>
    </div>
  );
}
